import { TMobileSignupForm } from './form';
import {
  getStartedFormToApplicationPutBody,
  companyFormToStatePutBody,
  personalFormToStatePutBody,
} from './mapping';
import {
  fetchPutAndGetState,
  usePutApplicationMutation,
} from '../../../api/lendingApi/lendingApiMutations';
import {
  useGetApplicationQuery,
  useGetStateQuery,
} from '../../../api/lendingApi/lendingApiQueries';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useLocale } from '../../../hooks/useLocale';
import { TGetStateResponseDE } from '../../../lapiOverrideTypesDE';
import logger from '../../../utils/logger';

export function useMobileSignupPersistence() {
  const stateKey = useContextStateKey();
  const { locale } = useLocale();
  const { state } = useGetStateQuery<TGetStateResponseDE>();
  const { application } = useGetApplicationQuery();
  const { mutatePutApplication } = usePutApplicationMutation();

  // The context should be resolved well before these screens. If it isn't, the
  // stage PUT is the only backend write for this stage, so throw rather than
  // advance with silent data loss — useScreenValidation blocks, logs, and
  // surfaces the inline error, and the user can retry once the context loads.
  const contextNotReadyError = (stage: string) => {
    logger.error('MobileSignup: stage PUT blocked — context not ready', {
      tags: { team: 'de-introducer' },
      stage,
    });
    return new Error(
      `MobileSignup: ${stage} stage PUT attempted before context ready`,
    );
  };

  const putGetStartedStage = async (form: TMobileSignupForm) => {
    if (!stateKey) throw contextNotReadyError('get-started');
    await mutatePutApplication({
      body: getStartedFormToApplicationPutBody(application, form, locale),
      stateKey,
    });
  };

  const putCompanyStage = async (form: TMobileSignupForm) => {
    if (!stateKey || !state) throw contextNotReadyError('company');
    await fetchPutAndGetState({
      body: companyFormToStatePutBody(state, form),
      stateKey,
    });
  };

  const putPersonalStage = async (form: TMobileSignupForm) => {
    if (!stateKey || !state) throw contextNotReadyError('personal');
    await fetchPutAndGetState({
      body: personalFormToStatePutBody(state, form),
      stateKey,
    });
  };

  return { putGetStartedStage, putCompanyStage, putPersonalStage };
}
